// Pure table operations — extracted from TableElement so the data logic stays
// small, testable, and free of React. The TableElement component imports these.
import type {
  CellElement,
  CellRange,
  TableCellData,
  TableCellStyle,
  TableColumnData,
  TableConfig,
  TableConfigSnapshot,
  TableRowData,
} from './table-types';
import {DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, MIN_COL_WIDTH, MIN_ROW_HEIGHT} from './table-types';
import {canAlignInCell} from './cell-elements';

// ─── Range helpers ────────────────────────────────────────────────────────────

export function normalizeRange(r: CellRange): CellRange {
  return {
    startRow: Math.min(r.startRow, r.endRow),
    endRow: Math.max(r.startRow, r.endRow),
    startCol: Math.min(r.startCol, r.endCol),
    endCol: Math.max(r.startCol, r.endCol),
  };
}

export function isSingleCell(r: CellRange): boolean {
  return r.startRow === r.endRow && r.startCol === r.endCol;
}

export function isCellInRange(r: CellRange, ri: number, ci: number): boolean {
  const n = normalizeRange(r);
  return ri >= n.startRow && ri <= n.endRow && ci >= n.startCol && ci <= n.endCol;
}

export function singleRange(ri: number, ci: number): CellRange {
  return {startRow: ri, startCol: ci, endRow: ri, endCol: ci};
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

export function uid(): string {
  return crypto.randomUUID();
}

function makeCell(): TableCellData {
  return {id: uid(), content: ''};
}

function makeRow(numCols: number): TableRowData {
  return {id: uid(), cells: Array.from({length: numCols}, makeCell)};
}

function makeColumn(): TableColumnData {
  return {id: uid()};
}

/**
 * Bounds on the size a table can be inserted at (DERP-226 item 4), where the size
 * is typed in rather than picked off a grid.
 *
 * Columns stop at 12 because that is all an A4 page holds: 595px less the default
 * 40px margins leaves 515px, and a column cannot be narrower than MIN_COL_WIDTH
 * (40px). Rows are capped well above one page's worth (842px / DEFAULT_ROW_HEIGHT ≈ 23)
 * since a long table is legitimate and simply continues onto the next page; the
 * limit is only there to stop a mistyped 10000 from building a table that large.
 */
export const MAX_INIT_ROWS = 100;
export const MAX_INIT_COLS = 12;

/** Whether a typed row/column count can actually be inserted. */
export function isInsertableTableSize(numRows: number, numCols: number): boolean {
  return (
    Number.isInteger(numRows) &&
    Number.isInteger(numCols) &&
    numRows >= 1 &&
    numCols >= 1 &&
    numRows <= MAX_INIT_ROWS &&
    numCols <= MAX_INIT_COLS
  );
}

export function initTable(numRows: number, numCols: number): TableConfig {
  // Guarded here, not just at the picker: DERP-254 adds a second insertion path
  // (template migration), and a mistyped size must not build the table either way.
  if (!isInsertableTableSize(numRows, numCols)) {
    throw new Error(`table size out of bounds: ${numRows} × ${numCols}`);
  }
  return {
    rows: Array.from({length: numRows}, () => makeRow(numCols)),
    columns: Array.from({length: numCols}, makeColumn),
    cellRange: null,
  };
}

// ─── Navigation helpers ───────────────────────────────────────────────────────

// Returns the next visible (non-hidden) cell in the given direction, skipping
// over merged spans. Returns null when already at the boundary.
export function nextVisibleCell(
  rows: TableRowData[],
  ri: number,
  ci: number,
  direction: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight',
): {row: number; col: number} | null {
  const numRows = rows.length;
  const numCols = rows[0]?.cells.length ?? 0;
  const current = rows[ri]?.cells[ci];

  let r = ri;
  let c = ci;

  switch (direction) {
    case 'ArrowRight':
      c += !current?.hidden ? (current?.colSpan ?? 1) : 1;
      break;
    case 'ArrowDown':
      r += !current?.hidden ? (current?.rowSpan ?? 1) : 1;
      break;
    case 'ArrowLeft':
      c -= 1;
      break;
    case 'ArrowUp':
      r -= 1;
      break;
  }

  if (r < 0 || r >= numRows || c < 0 || c >= numCols) return null;

  const target = rows[r]?.cells[c];
  if (!target) return null;
  if (!target.hidden) return {row: r, col: c};

  // Landed on a hidden cell — find its merge anchor.
  return findMergeAnchor(rows, r, c);
}

// Tab-order traversal: visits only visible (non-hidden) cells, wrapping rows.
export function nextTabCell(
  rows: TableRowData[],
  ri: number,
  ci: number,
  backward: boolean,
): {row: number; col: number} | null {
  const numRows = rows.length;
  const numCols = rows[0]?.cells.length ?? 0;
  const current = rows[ri]?.cells[ci];
  const forwardSpan = !backward && !current?.hidden ? (current?.colSpan ?? 1) : 1;

  let r = ri;
  let c = backward ? ci - 1 : ci + forwardSpan;

  while (r >= 0 && r < numRows) {
    if (c >= 0 && c < numCols) {
      const cell = rows[r]?.cells[c];
      if (cell && !cell.hidden) return {row: r, col: c};
      if (backward) c--;
      else c++;
    } else if (backward) {
      r--;
      c = numCols - 1;
    } else {
      r++;
      c = 0;
    }
  }

  return null;
}

// ─── Pure table operations ────────────────────────────────────────────────────

export function tableAddRowAbove(cfg: TableConfig, rowIdx: number): TableConfig {
  // Split any vertical merge crossing the insertion boundary first, else the
  // new row lands inside a rowSpan and the merge rectangle no longer matches
  // its anchor's declared span (mirrors tableDeleteRow).
  const split = splitMergesIntersectingRow(cfg, rowIdx);
  const rows = [...split.rows];
  rows.splice(rowIdx, 0, makeRow(split.columns.length));
  return {...split, rows, cellRange: singleRange(rowIdx, split.cellRange?.startCol ?? 0)};
}

export function tableAddRowBelow(cfg: TableConfig, rowIdx: number): TableConfig {
  const split = splitMergesIntersectingRow(cfg, rowIdx);
  const rows = [...split.rows];
  rows.splice(rowIdx + 1, 0, makeRow(split.columns.length));
  return {...split, rows, cellRange: singleRange(rowIdx + 1, split.cellRange?.startCol ?? 0)};
}

export function tableAddColLeft(cfg: TableConfig, colIdx: number): TableConfig {
  // Split any horizontal merge crossing the insertion boundary first (mirrors
  // tableDeleteCol) so the new cell never lands inside an unadjusted colSpan.
  const split = splitMergesIntersectingCol(cfg, colIdx);
  const columns = [...split.columns];
  columns.splice(colIdx, 0, makeColumn());
  const rows = split.rows.map((row) => {
    const cells = [...row.cells];
    cells.splice(colIdx, 0, makeCell());
    return {...row, cells};
  });
  return {
    ...split,
    rows,
    columns,
    cellRange: singleRange(split.cellRange?.startRow ?? 0, colIdx),
  };
}

export function tableAddColRight(cfg: TableConfig, colIdx: number): TableConfig {
  const split = splitMergesIntersectingCol(cfg, colIdx);
  const columns = [...split.columns];
  columns.splice(colIdx + 1, 0, makeColumn());
  const rows = split.rows.map((row) => {
    const cells = [...row.cells];
    cells.splice(colIdx + 1, 0, makeCell());
    return {...row, cells};
  });
  return {
    ...split,
    rows,
    columns,
    cellRange: singleRange(split.cellRange?.startRow ?? 0, colIdx + 1),
  };
}

export function tableDeleteRow(cfg: TableConfig, rowIdx: number): TableConfig {
  if (cfg.rows.length <= 1) return cfg;
  const split = splitMergesIntersectingRow(cfg, rowIdx);
  const rows = split.rows.filter((_, i) => i !== rowIdx);
  const newRow = Math.min(rowIdx, rows.length - 1);
  return {...split, rows, cellRange: singleRange(newRow, split.cellRange?.startCol ?? 0)};
}

export function tableDeleteCol(cfg: TableConfig, colIdx: number): TableConfig {
  if (cfg.columns.length <= 1) return cfg;
  const split = splitMergesIntersectingCol(cfg, colIdx);
  const columns = split.columns.filter((_, i) => i !== colIdx);
  const rows = split.rows.map((row) => ({
    ...row,
    cells: row.cells.filter((_, i) => i !== colIdx),
  }));
  const newCol = Math.min(colIdx, columns.length - 1);
  return {
    ...split,
    rows,
    columns,
    cellRange: singleRange(split.cellRange?.startRow ?? 0, newCol),
  };
}

/** Delete an inclusive range of rows. Deletes from the bottom up so indices stay
 *  valid, reusing the single-row delete (which splits intersecting merges and
 *  never removes the last remaining row). */
export function tableDeleteRows(cfg: TableConfig, startRow: number, endRow: number): TableConfig {
  const lo = Math.min(startRow, endRow);
  const hi = Math.max(startRow, endRow);
  let next = cfg;
  for (let r = hi; r >= lo; r--) next = tableDeleteRow(next, r);
  const newRow = Math.max(0, Math.min(lo, next.rows.length - 1));
  return {...next, cellRange: singleRange(newRow, next.cellRange?.startCol ?? 0)};
}

/** Delete an inclusive range of columns (bottom-up, reusing the single-column delete). */
export function tableDeleteCols(cfg: TableConfig, startCol: number, endCol: number): TableConfig {
  const lo = Math.min(startCol, endCol);
  const hi = Math.max(startCol, endCol);
  let next = cfg;
  for (let c = hi; c >= lo; c--) next = tableDeleteCol(next, c);
  const newCol = Math.max(0, Math.min(lo, next.columns.length - 1));
  return {...next, cellRange: singleRange(next.cellRange?.startRow ?? 0, newCol)};
}

export function tableToggleHeaderRow(cfg: TableConfig, rowIdx: number): TableConfig {
  const rows = cfg.rows.map((row, ri) => (ri !== rowIdx ? row : {...row, isHeader: !row.isHeader}));
  return {...cfg, rows};
}

export function tableDuplicateRow(cfg: TableConfig, rowIdx: number): TableConfig {
  const sourceRow = cfg.rows[rowIdx];
  if (!sourceRow) return cfg;
  // A cell is hidden either because a horizontal colSpan (which we preserve)
  // covers it, or because a vertical rowSpan from a row above reaches into it
  // (which we drop). Unhide only the latter; blanket `hidden: false` would
  // reveal colSpan-covered cells and corrupt the within-row merge.
  let coveredByColSpan = 0;
  const newRow: TableRowData = {
    ...sourceRow,
    id: uid(),
    isHeader: false,
    cells: sourceRow.cells.map((cell) => {
      const hiddenByColSpan = coveredByColSpan > 0;
      if (coveredByColSpan > 0) coveredByColSpan -= 1;
      else if ((cell.colSpan ?? 1) > 1) coveredByColSpan = (cell.colSpan ?? 1) - 1;
      return {
        ...cell,
        id: uid(),
        hidden: hiddenByColSpan,
        rowSpan: undefined,
        // Preserve colSpan for within-row column merges.
      };
    }),
  };
  const rows = [...cfg.rows];
  rows.splice(rowIdx + 1, 0, newRow);
  return {...cfg, rows, cellRange: singleRange(rowIdx + 1, cfg.cellRange?.startCol ?? 0)};
}

export function tableDuplicateCol(cfg: TableConfig, colIdx: number): TableConfig {
  const sourceCol = cfg.columns[colIdx];
  if (!sourceCol) return cfg;
  const newCol: TableColumnData = {...sourceCol, id: uid()};
  const columns = [...cfg.columns];
  columns.splice(colIdx + 1, 0, newCol);
  const rows = cfg.rows.map((row) => {
    const sourceCell = row.cells[colIdx];
    const newCell: TableCellData = sourceCell
      ? {...sourceCell, id: uid(), hidden: false, colSpan: undefined, rowSpan: undefined}
      : makeCell();
    const cells = [...row.cells];
    cells.splice(colIdx + 1, 0, newCell);
    return {...row, cells};
  });
  return {
    ...cfg,
    rows,
    columns,
    cellRange: singleRange(cfg.cellRange?.startRow ?? 0, colIdx + 1),
  };
}

export function tableDistributeCols(cfg: TableConfig): TableConfig {
  const n = cfg.columns.length;
  if (n === 0) return cfg;
  const total = cfg.columns.reduce((sum, col) => sum + (col.width ?? DEFAULT_COL_WIDTH), 0);
  const w = Math.max(MIN_COL_WIDTH, Math.floor(total / n));
  const columns = cfg.columns.map((col) => ({...col, width: w}));
  return {...cfg, columns};
}

export function tableDistributeRows(cfg: TableConfig): TableConfig {
  const n = cfg.rows.length;
  if (n === 0) return cfg;
  const total = cfg.rows.reduce((sum, row) => sum + (row.height ?? DEFAULT_ROW_HEIGHT), 0);
  const h = Math.max(MIN_ROW_HEIGHT, Math.floor(total / n));
  const rows = cfg.rows.map((row) => ({...row, height: h}));
  return {...cfg, rows};
}

// ─── Merge / split ────────────────────────────────────────────────────────────

function findMergeAnchor(
  rows: TableRowData[],
  targetRi: number,
  targetCi: number,
): {row: number; col: number} | null {
  for (let r = 0; r <= targetRi; r++) {
    for (let c = 0; c < (rows[r]?.cells.length ?? 0); c++) {
      if (r === targetRi && c === targetCi) continue;
      const cell = rows[r]?.cells[c];
      if (!cell || cell.hidden) continue;
      const rowEnd = r + (cell.rowSpan ?? 1) - 1;
      const colEnd = c + (cell.colSpan ?? 1) - 1;
      if (r <= targetRi && targetRi <= rowEnd && c <= targetCi && targetCi <= colEnd) {
        return {row: r, col: c};
      }
    }
  }
  return null;
}

export function tableSplitCell(cfg: TableConfig, ri: number, ci: number): TableConfig {
  const anchor = cfg.rows[ri]?.cells[ci];
  if (!anchor || anchor.hidden) return cfg;
  const rowSpan = anchor.rowSpan ?? 1;
  const colSpan = anchor.colSpan ?? 1;
  if (rowSpan <= 1 && colSpan <= 1) return cfg;

  const rows = cfg.rows.map((row, rIdx) => {
    if (rIdx < ri || rIdx >= ri + rowSpan) return row;
    const cells = row.cells.map((cell, cIdx) => {
      if (cIdx < ci || cIdx >= ci + colSpan) return cell;
      if (rIdx === ri && cIdx === ci) {
        return {...cell, rowSpan: undefined, colSpan: undefined};
      }
      return {...cell, hidden: false};
    });
    return {...row, cells};
  });

  return {...cfg, rows, cellRange: singleRange(ri, ci)};
}

function splitMergesIntersectingRow(cfg: TableConfig, rowIdx: number): TableConfig {
  let result = cfg;
  for (let ri = 0; ri <= rowIdx; ri++) {
    for (let ci = 0; ci < (result.rows[ri]?.cells.length ?? 0); ci++) {
      const cell = result.rows[ri]?.cells[ci];
      if (!cell || cell.hidden) continue;
      if ((cell.rowSpan ?? 1) > 1 && rowIdx < ri + (cell.rowSpan ?? 1)) {
        result = tableSplitCell(result, ri, ci);
      }
    }
  }
  return result;
}

function splitMergesIntersectingCol(cfg: TableConfig, colIdx: number): TableConfig {
  let result = cfg;
  for (let ri = 0; ri < result.rows.length; ri++) {
    for (let ci = 0; ci <= colIdx; ci++) {
      const cell = result.rows[ri]?.cells[ci];
      if (!cell || cell.hidden) continue;
      if ((cell.colSpan ?? 1) > 1 && colIdx < ci + (cell.colSpan ?? 1)) {
        result = tableSplitCell(result, ri, ci);
      }
    }
  }
  return result;
}

export function canMerge(cfg: TableConfig, range: CellRange): boolean {
  const n = normalizeRange(range);
  if (isSingleCell(n)) return false;
  for (let ri = n.startRow; ri <= n.endRow; ri++) {
    for (let ci = n.startCol; ci <= n.endCol; ci++) {
      const cell = cfg.rows[ri]?.cells[ci];
      if (!cell) return false;
      if (cell.hidden) {
        const anchor = findMergeAnchor(cfg.rows, ri, ci);
        if (!anchor) return false;
        const ac = cfg.rows[anchor.row]?.cells[anchor.col];
        if (!ac) return false;
        if (
          anchor.row < n.startRow ||
          anchor.col < n.startCol ||
          anchor.row + (ac.rowSpan ?? 1) - 1 > n.endRow ||
          anchor.col + (ac.colSpan ?? 1) - 1 > n.endCol
        )
          return false;
      } else {
        if (ri + (cell.rowSpan ?? 1) - 1 > n.endRow) return false;
        if (ci + (cell.colSpan ?? 1) - 1 > n.endCol) return false;
      }
    }
  }
  return true;
}

export function tableMergeCells(cfg: TableConfig, range: CellRange): TableConfig {
  const n = normalizeRange(range);
  const rowSpan = n.endRow - n.startRow + 1;
  const colSpan = n.endCol - n.startCol + 1;

  const rows = cfg.rows.map((row, ri) => {
    if (ri < n.startRow || ri > n.endRow) return row;
    const cells = row.cells.map((cell, ci) => {
      if (ci < n.startCol || ci > n.endCol) return cell;
      if (ri === n.startRow && ci === n.startCol) {
        return {...cell, rowSpan, colSpan, hidden: false};
      }
      return {...cell, hidden: true, rowSpan: undefined, colSpan: undefined};
    });
    return {...row, cells};
  });

  return {...cfg, rows, cellRange: singleRange(n.startRow, n.startCol)};
}

// ─── Cell style helpers ───────────────────────────────────────────────────────

/**
 * How the cell's alignment reaches the ELEMENT box in it — an image input, a
 * text input, a signature pad.
 *
 * `textAlign` aligns text runs and nothing else: a box with a width of its own
 * is a block, and `text-align` does not move one. So the align button in the
 * cell toolbar looked broken on every cell holding an element — the author
 * pressed centre, the text in the cell shifted, and the box stayed left.
 *
 * Expressed as `justify-content`, because that is the one rule that distributes
 * the free space in a row and leaves a sized child's WIDTH alone. `align-items`
 * would have resized them instead, and a percentage width inside a
 * shrink-to-fit parent is how an in-cell box collapses to nothing.
 *
 * **Aligning never resizes**, which is what `canAlignInCell` is for and why it
 * gates this and not only the toolbar. An element with no width of its own
 * fills its cell by design and turns shrink-to-fit the moment its wrapper
 * becomes a flex row — so a centred cell holding a checkbox, a dropdown or a
 * text answer leaves them exactly as they were rather than squeezing them to
 * their content. The same predicate decides whether the control is offered, so
 * the button and the layout cannot disagree about what alignment reaches.
 *
 * `canAlignInCell` also excludes a `logo`, which aligns itself through
 * `config.align`. The cell layering a second alignment over it would take the
 * first one's effect away — the inner flex shrinks to its image and its own
 * `justify-content` stops meaning anything.
 *
 * **The element's own `align` wins over the cell's.** The cell answers for its
 * whole stack, which is what an author formatting a cell means; an element the
 * author pointed at and aligned is a narrower statement about that one box, so
 * it takes precedence. Neither set means follow the cell, which is every
 * element authored before this existed.
 *
 * Empty for the default, so a cell nobody has aligned renders exactly the block
 * layout it always did. Only an explicit centre or right turns the stack into a
 * flex row — which is the only case where anything was asked for.
 */
export function cellItemStyle(
  s: TableCellStyle | undefined,
  el: CellElement | undefined,
): React.CSSProperties {
  if (!el || !canAlignInCell(el)) return {};
  const align = el.align ?? s?.textAlign;
  if (align !== 'center' && align !== 'right') return {};
  return {display: 'flex', justifyContent: align === 'center' ? 'center' : 'flex-end'};
}

export function buildCellStyle(s?: TableCellStyle): {
  td: React.CSSProperties;
  text: React.CSSProperties;
} {
  const bColor = s?.borderColor ?? '#000000';
  // Per-side style, falling back to the cell-level style, then solid.
  const borderVal = (w: number | undefined, sideStyle?: string): string | undefined => {
    if (w === undefined) return undefined; // don't override the CSS-class default
    return w === 0 ? 'none' : `${w}px ${sideStyle ?? s?.borderStyle ?? 'solid'} ${bColor}`;
  };

  return {
    td: {
      // A column is fixed (`table-fixed`), so a word longer than it — a serial
      // number, a URL, a `{{variable}}` — ran past the cell into the next one.
      // `anywhere` breaks only such a word, only where it must, and is inherited,
      // so every bit of text in the cell wraps: its content, a heading, a
      // checkbox's options. Set here because every cell, on every surface,
      // takes its box from this.
      overflowWrap: 'anywhere',
      verticalAlign: s?.verticalAlign ?? 'top',
      backgroundColor: s?.backgroundColor ?? undefined,
      padding: s?.padding !== undefined ? `${s.padding}px` : '4px 6px',
      borderTop: borderVal(s?.borderTopWidth, s?.borderTopStyle),
      borderRight: borderVal(s?.borderRightWidth, s?.borderRightStyle),
      borderBottom: borderVal(s?.borderBottomWidth, s?.borderBottomStyle),
      borderLeft: borderVal(s?.borderLeftWidth, s?.borderLeftStyle),
    },
    text: {
      fontFamily: s?.fontFamily || undefined,
      // A NUMBER, which React writes as px. Kept numeric because the text box's
      // floor is derived from it (`textBoxMinHeight`) and re-parsing "14px" to
      // get it back would be a second idea of what the size is.
      fontSize: s?.fontSize,
      fontWeight: s?.bold ? 'bold' : undefined,
      fontStyle: s?.italic ? 'italic' : undefined,
      textDecoration: s?.underline ? 'underline' : undefined,
      color: s?.color || undefined,
      textAlign: (s?.textAlign as React.CSSProperties['textAlign']) ?? undefined,
    },
  };
}

export function updateCellStyle(
  cfg: TableConfig,
  row: number,
  col: number,
  patch: Partial<TableCellStyle>,
): TableConfig {
  const rows = cfg.rows.map((r, ri) =>
    ri !== row
      ? r
      : {
          ...r,
          cells: r.cells.map((c, ci) => (ci !== col ? c : {...c, style: {...c.style, ...patch}})),
        },
  );
  return {...cfg, rows};
}

export function updateRangeStyle(
  cfg: TableConfig,
  range: CellRange,
  patch: Partial<TableCellStyle>,
): TableConfig {
  const n = normalizeRange(range);
  const rows = cfg.rows.map((row, ri) => {
    if (ri < n.startRow || ri > n.endRow) return row;
    const cells = row.cells.map((cell, ci) =>
      ci < n.startCol || ci > n.endCol ? cell : {...cell, style: {...cell.style, ...patch}},
    );
    return {...row, cells};
  });
  return {...cfg, rows};
}

/**
 * Project a config onto the snapshot `TableElement`'s latest-value ref holds.
 *
 * The single builder matters: the ref is refreshed both by the post-render sync
 * effect AND by `update()` before it hands a commit upstream (so two commits in
 * one tick don't read pre-commit state and silently discard the first). Two
 * hand-written projections would be two places to remember a new field.
 */
export function toTableSnapshot(cfg: TableConfig): TableConfigSnapshot {
  return {
    rows: cfg.rows ?? [],
    columns: cfg.columns ?? [],
    cellRange: cfg.cellRange ?? null,
    tableWidth: cfg.tableWidth,
    tableAlign: cfg.tableAlign,
    selectedElementIndex: cfg.selectedElementIndex ?? null,
  };
}
